import type { AtlasUi, UiColorPair } from '../utils/themeAugmentation';
import type { PaletteRole } from '../../utils/paletteRoleColors';

// Shell Core Elements Master Policy, p. 81: "Only use colours from the Shell palette."
// Swatches: primary p. 35; greys p. 42; red/yellow pp. 52-53;
// Sunset p. 55, Earth p. 56, Sunrise p. 57, Stone p. 58, Sand p. 59,
// Forest p. 62 and Ocean p. 63. See shell-palette-audit.md for role decisions.
export const shellColors = {
  white: '#FFFFFF', yellow: '#FFC600', yellow100: '#FFDF55', yellow300: '#D6A200',
  yellow50: '#FFF7B4', yellow800: '#443100',
  red: '#DD1D21', red50: '#FFF2F0', red300: '#FF887B', red400: '#FB594E', red600: '#C5000D', red800: '#6F0000',
  grey50: '#F5F5F5', grey100: '#E0E0E0', grey200: '#C0C0C0', grey300: '#AAAAAA', grey400: '#919191',
  grey500: '#757575', grey600: '#616161', grey700: '#4A4A4A', grey800: '#343434', grey900: '#292929',
  forest50: '#EFF7F2', forest200: '#97CDAF', forest300: '#6FB993', forest500: '#008557', forest700: '#005531', forest800: '#003C21',
  sunrise50: '#FFF3E5', sunrise200: '#FFB15B', sunrise300: '#ED8A00', sunrise600: '#964E00', sunrise800: '#542800',
  ocean50: '#EDF7F9', ocean100: '#C0E6EC', ocean200: '#81CCD9', ocean600: '#006B7B', ocean700: '#00525E', ocean800: '#003A44',
  earth300: '#C3A494', earth400: '#B28873', earth700: '#743A1E',
  stone300: '#B6B099', sunset200: '#FFAD92', sunset300: '#FF8966', sunset500: '#D54410', sand100: '#E1DDA9',
} as const;

const c = shellColors;
const pair = (color: string, backgroundColor: string): UiColorPair => ({ color, backgroundColor });

export function createShellUi(mode: 'light' | 'dark'): AtlasUi {
  const dark = mode === 'dark';
  // p. 74: "Use Foundations, Frameworks and Fabrics" ... "in light and dark modes."
  // These role assignments and dark-mode inversions are application choices,
  // not official Fabrics tokens. Status meanings are intentionally unchanged.
  const subtle: Record<PaletteRole, UiColorPair> = {
    primary: pair(c.grey700, dark ? c.yellow : c.yellow50),
    success: dark ? pair(c.forest200, c.forest800) : pair(c.forest700, c.forest50),
    warning: dark ? pair(c.sunrise200, c.sunrise800) : pair(c.sunrise600, c.sunrise50),
    info: dark ? pair(c.ocean200, c.ocean800) : pair(c.ocean600, c.ocean50),
    error: dark ? pair(c.red300, c.red800) : pair(c.red600, c.red50),
    neutral: dark ? pair(c.grey300, c.grey900) : pair(c.grey700, c.grey50),
  };
  const solid: Record<PaletteRole, UiColorPair> = {
    primary: pair(c.grey700, c.yellow),
    success: dark ? pair(c.grey900, c.forest300) : pair(c.white, c.forest500),
    warning: dark ? pair(c.grey700, c.sunrise200) : pair(c.grey900, c.sunrise300),
    info: dark ? pair(c.grey700, c.ocean200) : pair(c.white, c.ocean600),
    error: dark ? pair(c.grey900, c.red300) : pair(c.white, c.red),
    neutral: subtle.neutral,
  };
  return {
    // p. 302: "Icons should be designed using Shell Grey 700."
    // Dark surfaces require an accessible neutral inversion.
    icon: dark ? c.grey300 : c.grey700,
    iconBackground: dark ? c.grey900 : c.grey50,
    // p. 81: "at least 4.5:1 w.r.t the background". Red 500 fails on
    // the filter ribbon's grey overlay; Red 600 keeps text actions readable.
    link: dark ? c.red300 : c.red600,
    linkHover: dark ? c.white : c.red600,
    focus: dark ? c.grey100 : c.grey700,
    control: dark ? c.grey100 : c.grey700,
    selectedForeground: dark ? c.grey100 : c.grey700,
    selectedBackground: dark ? c.yellow800 : c.yellow50,
    selectedBorder: dark ? c.yellow : c.grey700,
    chartOutline: dark ? c.grey100 : c.grey700,
    badgeColors: Object.fromEntries(Object.keys(subtle).map(role => [role, {
      subtle: subtle[role as PaletteRole], solid: solid[role as PaletteRole],
    }])) as AtlasUi['badgeColors'],
    statusRoles: {
      active: 'success', validada: 'success', conciliada: 'success', pagada: 'success', finalizada: 'success',
      expiring: 'warning', incompleta: 'warning', enCurso: 'warning',
      future: 'info', nueva: 'info', emitida: 'info', generada: 'info',
      rechazada: 'error', impago: 'error',
      historical: 'neutral', borrador: 'neutral', descartada: 'neutral',
    },
  };
}
